import {
  type IAdminProduct,
  type IProductNames,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import { type IProductsState } from './types';

export const preloadedState: IProductsState = {
  ...asyncStateInitial,
  products: undefined,
  productsShort: undefined,
  newProduct: undefined,
};

const productsSlice = createSlice({
  name: 'Products',
  initialState: preloadedState,
  reducers: {
    productAdd: {
      reducer(state) {
        startLoading(state);
      },
      prepare(name: string, code: string, productDownloadedUUID?: string) {
        return {
          payload: {
            name,
            productDownloaded: { uuid: productDownloadedUUID },
            languageType: { code },
          },
        };
      },
    },
    productAddResponse: {
      reducer(state, action: { payload: { product: IAdminProduct | null } }) {
        state.newProduct = action.payload.product;

        if (action.payload.product) {
          if (Array.isArray(state.products)) {
            state.products.unshift(action.payload.product);
          } else {
            state.products = [action.payload.product];
          }
        }

        finishLoading(state);
      },
      prepare(product: IAdminProduct | null) {
        return { payload: { product } };
      },
    },
    productsLoadAll: {
      reducer(state) {
        startLoading(state);
        state.products = undefined;
      },
      prepare(isActiveProducts: boolean) {
        return { payload: { isActiveProducts } };
      },
    },
    productsLoadAllResponse: {
      reducer(state, action: { payload: { products: IAdminProduct[] } }) {
        state.products = action.payload.products;
        finishLoading(state);
      },
      prepare(products: IAdminProduct[]) {
        return { payload: { products } };
      },
    },
    productsLoadAllShort(state) {
      startLoading(state);
      state.productsShort = undefined;
    },
    productsLoadAllShortResponse: {
      reducer(state, action: { payload: { products: IProductNames[] } }) {
        state.productsShort = action.payload.products;
        finishLoading(state);
      },
      prepare(products: IProductNames[]) {
        return { payload: { products } };
      },
    },
    productsLoadByCategory: {
      reducer(state) {
        startLoading(state);
        state.products = undefined;
      },
      prepare(categoryUUID: string) {
        return { payload: { category: { uuid: categoryUUID } } };
      },
    },
    productsLoadByCategoryResponse: {
      reducer(state, action: { payload: { products: IAdminProduct[] } }) {
        state.products = action.payload.products;
        finishLoading(state);
      },
      prepare(products: IAdminProduct[]) {
        return { payload: { products } };
      },
    },
    productsGetByMPN: {
      reducer(state) {
        startLoading(state);
        state.products = undefined;
      },
      prepare(mpn: string) {
        return { payload: { mpn } };
      },
    },
    productsGetByMPNResponse: {
      reducer(state, action: { payload: { products: IAdminProduct[] } }) {
        state.products = action.payload.products;
        finishLoading(state);
      },
      prepare(products: IAdminProduct[]) {
        return { payload: { products } };
      },
    },
    productsGetByGTIN: {
      reducer(state) {
        startLoading(state);
        state.products = undefined;
      },
      prepare(gtin: string) {
        return { payload: { gtin } };
      },
    },
    productsGetByGTINResponse: {
      reducer(state, action: { payload: { products: IAdminProduct[] } }) {
        state.products = action.payload.products;
        finishLoading(state);
      },
      prepare(products: IAdminProduct[]) {
        return { payload: { products } };
      },
    },
    productsLoadByBrand: {
      reducer(state) {
        startLoading(state);
        state.products = undefined;
      },
      prepare(brandUUID: string) {
        return { payload: { brand: { uuid: brandUUID } } };
      },
    },
    productsLoadByBrandResponse: {
      reducer(state, action: { payload: { products: IAdminProduct[] } }) {
        state.products = action.payload.products;
        finishLoading(state);
      },
      prepare(products: IAdminProduct[]) {
        return { payload: { products } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Products'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  productAdd,
  productAddResponse,
  productsLoadAll,
  productsLoadAllResponse,
  productsLoadAllShort,
  productsLoadAllShortResponse,
  productsLoadByCategory,
  productsLoadByCategoryResponse,
  productsGetByMPN,
  productsGetByMPNResponse,
  productsGetByGTIN,
  productsGetByGTINResponse,
  productsLoadByBrand,
  productsLoadByBrandResponse,
} = productsSlice.actions;
export default productsSlice.reducer;
